/**
 * CORE-1200 — диагностируемость сетевых отказов авторизации.
 *
 * 02.09.2026 человек шёл по ссылке в Live, смотрел гостем, нажимал «войти» и
 * получал `Failed to fetch (api.agapella.io)`. В Sentry за это время не оказалось
 * ничего: поток `auth` вызывает `captureError`, но доставка события клиентом не
 * гарантирована ровно тогда, когда она нужнее всего — сеть уже не работает, а
 * ingest Sentry вдобавок режется расширениями.
 *
 * Поэтому диагностика тут держится на ДВУХ независимых ногах:
 *
 *  1. **Сначала first-party канал** — очередь CORE-604 (`netTelemetry`). Она шлёт
 *     батч одновременно на `api.agapella.io` И `api-ru.agapella.io`, переживает
 *     перезагрузку вкладки (localStorage), досылает beacon'ом на уход и дедупится
 *     по `clientEventId`. Это единственная нога, которая работает и в РФ, и на
 *     нативе, и при вырезанном Sentry.
 *  2. **Потом Sentry** — теми же полями, чтобы разбор шёл там, где команда уже
 *     смотрит. Через тоннель на своём домене (см. `App.tsx`), чтобы блокировщик
 *     не съел событие.
 *
 * Обе ноги подписаны одним коротким кодом. Он же показывается человеку — «назовите
 * код поддержке» — и он же уходит в заголовке запроса, чтобы серверная запись
 * села рядом с клиентской.
 *
 * ── Чего здесь намеренно НЕТ ────────────────────────────────────────────────
 *
 * Тикет просит различать DNS / TCP / TLS / CORS. **Браузер этого не отдаёт**: все
 * четыре случая приходят одним непрозрачным `TypeError: Failed to fetch`, без
 * кода и без причины — так устроен fetch, и обойти это со стороны страницы
 * нечем. Врать классификацией хуже, чем не знать: разбор пошёл бы по выдуманному
 * признаку. Поэтому `network` — честно один класс, а разделяют его уже поля,
 * которые измеримы: дошёл ли запрос до сервера (есть ли серверная запись по
 * этому коду), был ли браузер офлайн, какой хост был выбран, сколько заняло.
 *
 * И здесь никогда нет пароля, email, JWT и тела запроса — ни в событии, ни в
 * Sentry, ни в коде для человека.
 */
import { Platform } from 'react-native';

import { buildParts } from '../config/buildInfo';
import { captureError } from '../utils/sentry';
import { isRuActive } from './apiEndpoint';
import { liveIdFromRoute, newCorrelationCode, sanitizeRoute } from './authCorrelation';
import { trackAuthAttempt, trackAuthOutcome } from './netTelemetry';

export type AuthMethod = 'password' | 'register' | 'passkey' | 'google' | 'apple';

/**
 * Классы отказа, которые действительно различимы на клиенте.
 *
 * `network` покрывает DNS/TCP/TLS/CORS — см. преамбулу: браузер их не разделяет.
 */
export type AuthErrorKind =
  | 'http'      // сервер ответил статусом — запрос дошёл
  | 'timeout'   // наш таймаут или TimeoutError
  | 'abort'     // отменено (уход со страницы, повторная отправка)
  | 'offline'   // navigator.onLine === false на момент отказа
  | 'network'   // TypeError от fetch: DNS/TCP/TLS/CORS, неразличимо
  | 'parse'     // ответ пришёл, но тело не разобрать
  | 'unknown';

export interface AuthAttempt {
  /** Короткий код: он же correlationId, он же то, что видит человек. */
  code: string;
  method: AuthMethod;
  startedAt: number;
  /** Маршрут, с которого пришли (`returnTo`), без query. */
  sourceRoute: string | null;
  /** Live/room id, вытащенный из маршрута, если он там есть. */
  liveId: string | null;
}


/** Онлайн ли браузер на этот момент. `null` там, где вопрос не имеет смысла. */
function onlineStatus(): boolean | null {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return null;
  return typeof navigator.onLine === 'boolean' ? navigator.onLine : null;
}

/**
 * Классификация отказа. Порядок важен: статус старше всего остального — если
 * сервер ответил, спор о причине закончен, запрос дошёл.
 */
export function classifyAuthError(err: unknown): AuthErrorKind {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  if (typeof status === 'number' && status > 0) return 'http';

  const name = (err as { name?: unknown } | null | undefined)?.name;
  if (name === 'TimeoutError') return 'timeout';
  if (name === 'AbortError') return 'abort';

  const message = String((err as { message?: unknown } | null | undefined)?.message ?? '');
  if (/Не удалось обработать ответ сервера/i.test(message)) return 'parse';

  // Офлайн проверяем ДО TypeError: «сети нет» — это ответ, а «Failed to fetch» —
  // ещё нет. Иначе честный офлайн утонул бы в общей куче network.
  if (onlineStatus() === false) return 'offline';

  if (err instanceof TypeError || /Failed to fetch|Network request failed/i.test(message)) {
    return 'network';
  }
  return 'unknown';
}

/** Общая шапка события: сборка, платформа, выбранный хост, онлайн. */
function envelope(attempt: AuthAttempt): Record<string, unknown> {
  const build = buildParts();
  return {
    correlationId: attempt.code,
    method: attempt.method,
    sourceRoute: attempt.sourceRoute,
    liveId: attempt.liveId,
    apiHost: isRuActive() ? 'api-ru' : 'direct',
    online: onlineStatus(),
    platform: build.platform,
    release: build.sha || build.version,
    branch: build.branch || undefined,
  };
}

/**
 * Начало попытки. Возвращает дескриптор, который надо донести до `finish*`.
 *
 * Событие пишется ДО запроса намеренно: если вкладку закроют на висящем fetch,
 * в базе всё равно останется «попытка была, вот с какого маршрута и на какой
 * хост» — а отсутствие парного исхода само по себе диагноз.
 */
export function beginAuthAttempt(input: {
  method: AuthMethod;
  returnTo?: string | null;
}): AuthAttempt {
  const sourceRoute = sanitizeRoute(input.returnTo);
  const attempt: AuthAttempt = {
    code: newCorrelationCode(),
    method: input.method,
    startedAt: Date.now(),
    sourceRoute,
    liveId: liveIdFromRoute(sourceRoute),
  };
  try {
    trackAuthAttempt(envelope(attempt));
  } catch {
    // Телеметрия не имеет права уронить вход.
  }
  return attempt;
}

/**
 * Пришить код к ошибке, которая летит дальше вызывающему.
 *
 * Экран входа уже ловит эту ошибку, чтобы показать текст — пусть заодно
 * получит и код: иначе пришлось бы менять сигнатуру `login` и тащить код
 * отдельным каналом через все пять методов. Свойство необязательное, и его
 * отсутствие ничего не ломает: у ошибок, брошенных мимо этих путей, кода
 * просто нет.
 *
 * Возвращаем ту же ошибку, а не новую: подмена стёрла бы стек и `code`, по
 * которым экран выбирает локализованный текст.
 */
export function stampCorrelation<T>(err: T, code: string): T {
  try {
    (err as { correlationId?: string }).correlationId = code;
  } catch {
    // Заморожённый объект — не беда, код всё равно уже в событии и в Sentry.
  }
  return err;
}

/** Успех: замыкает пару, чтобы «попытка без исхода» осталась признаком обрыва. */
export function finishAuthSuccess(attempt: AuthAttempt): void {
  try {
    trackAuthOutcome({
      ...envelope(attempt),
      outcome: 'ok',
      durationMs: Date.now() - attempt.startedAt,
    });
  } catch {
    /* никогда не мешаем входу */
  }
}

/**
 * Отказ. Пишет в first-party канал ВСЕГДА, а в Sentry — по решению вызывающего
 * (`report`): «пользователь опечатался в пароле» чинится не кодом и в Sentry не
 * нужен (CORE-1099), но в диагностическом канале он полезен — по нему видно, что
 * запрос вообще дошёл.
 */
export function finishAuthFailure(
  attempt: AuthAttempt,
  err: unknown,
  opts?: { report?: boolean },
): AuthErrorKind {
  const kind = classifyAuthError(err);
  const status = (err as { status?: unknown } | null | undefined)?.status;
  const httpStatus = typeof status === 'number' ? status : null;
  const durationMs = Date.now() - attempt.startedAt;
  const base = envelope(attempt);

  try {
    trackAuthOutcome({
      ...base,
      outcome: 'error',
      errorKind: kind,
      httpStatus,
      durationMs,
      // Код ошибки от бэка (INVALID_CREDENTIALS и т.п.) — не PII и сильно
      // сокращает разбор. Тело и заголовки не берём вовсе.
      errorCode: (err as { code?: unknown } | null | undefined)?.code,
    });
  } catch {
    /* никогда не мешаем входу */
  }

  if (opts?.report) {
    captureError(err, {
      tags: {
        flow: 'auth',
        method: attempt.method,
        correlation_id: attempt.code,
        error_kind: kind,
      },
      extra: { ...base, errorKind: kind, httpStatus, durationMs },
    });
  }

  return kind;
}
